import React from 'react';
import classNames from 'classnames';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { QueryClientProvider } from 'react-query';
import BarcodeScannerPage from 'pages/BarcodeScannerPage';
import ForgotPasswordPage from 'pages/ForgotPasswordPage';
import HelpPage from 'pages/HelpPage';
import AboutPage from 'pages/AboutPage';
import NotificationsUnsubscribePage from 'pages/NotificationsUnsubscribePage';
import { ClassifiedRulesPage, PrivacyPage, TermsPage } from 'pages/StaticLegalPages';
import StandaloneFooter from 'components/StandaloneFooter';
import { ChatRoutes } from './ChatApp';
import Header from './components/Header';
import PageContainer from './components/ibok-core/module/PageContainer';
import ConfirmEmailPage from './pages/ConfirmEmailPage';
import BookDetailsPage from './pages/BookDetailsPage';
import CreateClassifiedPage from './pages/CreateClassifiedPage';
import Frontpage from './pages/Frontpage';
import LoginPage from './pages/LoginPage';
import MyPage from './pages/MyPage';
import RegisterUserPage from './pages/RegisterUserPage';
import SearchPage from './pages/SearchPage';
import UsersRequireName from './pages/UsersRequireName';
import AdministerClassifiedsPage from './pages/AdministerClassifiedsPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import MineClassifiedsPage from './pages/MineClassifiedsPage';
import NotificationsPage from './pages/NotificationsPage';
import SellerPage from './pages/SellerPage';
import SettingsPage from './pages/SettingsPage';
import { queryClient } from './queryClient';
import { MessageReplyQueueProvider } from './contexts/messageReplyQueueContext';
import useBrowserAuthMe from './hooks/useBrowserAuthMe';
import { getQueryParamByName } from './utils/urlDecoder';
import './StandaloneApp.less';
var ReactContextWrapper = function (_a) {
    var children = _a.children;
    return (React.createElement(MessageReplyQueueProvider, null, children));
};
var StandaloneHeader = function () {
    var user = useBrowserAuthMe().user;
    var isWelcomePage = window.location.pathname === '/';
    return React.createElement("header", { className: classNames('pageheader', { 'pageheader--is-landing': isWelcomePage }) },
        React.createElement(Header, { isLoggedIn: !!user, isWelcomePage: isWelcomePage }));
};
var LoginRoute = function () { return (React.createElement(PageContainer, null,
    React.createElement(LoginPage, null))); };
var RegisterRoute = function () { return (React.createElement(PageContainer, null,
    React.createElement(RegisterUserPage, null))); };
var RedirectingRoute = function (_a) {
    var to = _a.to;
    React.useEffect(function () {
        window.location.href = to;
    }, [to]);
    return null;
};
var getCurrentPath = function () { return "" + window.location.pathname + window.location.search; };
var getLoginPathForCurrentRoute = function () { return "/logg_inn?redirect=" + encodeURIComponent(getCurrentPath()); };
var getRegisterNamePathForCurrentRoute = function () { return ("/registrer_navn?redirect=" + encodeURIComponent(getCurrentPath())); };
var getRegisterNameRedirect = function () {
    var redirect = getQueryParamByName('redirect');
    if (!redirect || !redirect.startsWith('/') || redirect.startsWith('//') || redirect.startsWith('/registrer_navn')) {
        return '/chat';
    }
    return redirect;
};
var ChatRoute = function () {
    var _a = useBrowserAuthMe(), error = _a.error, isLoading = _a.isLoading, user = _a.user;
    if (isLoading) {
        return React.createElement(PageContainer, null,
            React.createElement("div", { className: "standalone-react__status" },
                React.createElement("h1", null, "Laster samtaler")));
    }
    if (!user || error) {
        return React.createElement(RedirectingRoute, { to: "/logg_inn" });
    }
    if (!user.name) {
        return React.createElement(RedirectingRoute, { to: getRegisterNamePathForCurrentRoute() });
    }
    return React.createElement(PageContainer, null,
        React.createElement(ChatRoutes, null));
};
var CreateClassifiedRoute = function () {
    var _a = useBrowserAuthMe(), error = _a.error, isLoading = _a.isLoading, user = _a.user;
    if (isLoading) {
        return React.createElement(PageContainer, null,
            React.createElement("div", { className: "standalone-react__status" },
                React.createElement("h1", null, "Laster annonseverkt\u00F8y")));
    }
    if (!user || error) {
        return React.createElement(RedirectingRoute, { to: getLoginPathForCurrentRoute() });
    }
    if (!user.name) {
        return React.createElement(RedirectingRoute, { to: getRegisterNamePathForCurrentRoute() });
    }
    return React.createElement(PageContainer, null,
        React.createElement(CreateClassifiedPage, null));
};
var MyPageRoute = function () {
    var _a = useBrowserAuthMe(), error = _a.error, isLoading = _a.isLoading, user = _a.user;
    if (isLoading) {
        return React.createElement(PageContainer, null,
            React.createElement("div", { className: "standalone-react__status" },
                React.createElement("h1", null, "Laster min side")));
    }
    if (!user || error) {
        return React.createElement(RedirectingRoute, { to: getLoginPathForCurrentRoute() });
    }
    if (!user.name) {
        return React.createElement(RedirectingRoute, { to: getRegisterNamePathForCurrentRoute() });
    }
    return React.createElement(MyPage, null);
};
var RegisterNameRoute = function () {
    var _a = useBrowserAuthMe(), error = _a.error, isLoading = _a.isLoading, user = _a.user;
    if (isLoading) {
        return React.createElement(PageContainer, null,
            React.createElement("div", { className: "standalone-react__status" },
                React.createElement("h1", null, "Laster navneskjema")));
    }
    if (!user || error) {
        return React.createElement(RedirectingRoute, { to: getLoginPathForCurrentRoute() });
    }
    if (user.name) {
        return React.createElement(RedirectingRoute, { to: getRegisterNameRedirect() });
    }
    return React.createElement(PageContainer, null,
        React.createElement(UsersRequireName, null));
};
/**
 * Route-wrapper which requires an authenticated user,
 * and redirects to the login page (with a return-redirect)
 * if the visitor is not signed in.
 * */
var AuthenticatedPageRoute = function (_a) {
    var children = _a.children, loadingTitle = _a.loadingTitle;
    var _b = useBrowserAuthMe(), error = _b.error, isLoading = _b.isLoading, user = _b.user;
    if (isLoading) {
        return React.createElement(PageContainer, null,
            React.createElement("div", { className: "standalone-react__status" },
                React.createElement("h1", null, loadingTitle)));
    }
    if (!user || error) {
        return React.createElement(RedirectingRoute, { to: getLoginPathForCurrentRoute() });
    }
    return React.createElement(React.Fragment, null, children);
};
var MineClassifiedsRoute = function () { return (React.createElement(AuthenticatedPageRoute, { loadingTitle: "Laster annonsene dine" },
    React.createElement(MineClassifiedsPage, null))); };
var AdministerClassifiedsRoute = function (_a) {
    var match = _a.match;
    return (React.createElement(AuthenticatedPageRoute, { loadingTitle: "Laster annonsene dine" },
        React.createElement(AdministerClassifiedsPage, { status: match.params.status })));
};
var NotificationsRoute = function () { return (React.createElement(AuthenticatedPageRoute, { loadingTitle: "Laster bokvarslingene dine" },
    React.createElement(NotificationsPage, null))); };
var SettingsRoute = function () { return (React.createElement(AuthenticatedPageRoute, { loadingTitle: "Laster innstillingene dine" },
    React.createElement(SettingsPage, null))); };
var ChangePasswordRoute = function (_a) {
    var match = _a.match;
    return (React.createElement(ChangePasswordPage, { activationCode: match.params.code }));
};
var parsePositiveInt = function (value, fallback) {
    var parsed = parseInt(value || '', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};
var SellerRoute = function (_a) {
    var match = _a.match;
    var page = parsePositiveInt(getQueryParamByName('page'), 1);
    var limit = Math.min(parsePositiveInt(getQueryParamByName('limit'), 30), 200);
    return React.createElement(SellerPage, { sellerId: match.params.id, page: page, limit: limit });
};
var NotFoundPage = function () { return (React.createElement(PageContainer, null,
    React.createElement("div", { className: "standalone-react__status" },
        React.createElement("h1", null, "Finner ikke siden"),
        React.createElement("p", null, "Siden du leter etter finnes ikke, eller har blitt flyttet."),
        React.createElement("a", { href: "/" }, "Til forsiden")))); };
var BookDetailsRoute = function (_a) {
    var match = _a.match;
    return (React.createElement(BookDetailsPage, { isbn: match.params.isbn }));
};
var StandaloneRoutes = function () { return (React.createElement(Switch, null,
    React.createElement(Route, { exact: true, path: "/", component: Frontpage }),
    React.createElement(Route, { exact: true, path: "/logg_inn", component: LoginRoute }),
    React.createElement(Route, { exact: true, path: "/registrer", component: RegisterRoute }),
    React.createElement(Route, { exact: true, path: "/registrer_navn", component: RegisterNameRoute }),
    React.createElement(Route, { exact: true, path: "/min_side", component: MyPageRoute }),
    React.createElement(Route, { exact: true, path: ['/sett_inn_annonse', '/sett_inn_annonse/'], component: CreateClassifiedRoute }),
    React.createElement(Route, { exact: true, path: ['/sok', '/sok/'], component: SearchPage }),
    React.createElement(Route, { exact: true, path: ['/bok/:isbn(\\d{10,13})', '/bok/:isbn(\\d{10,13})/'], component: BookDetailsRoute }),
    React.createElement(Route, { exact: true, path: "/chat", component: ChatRoute }),
    React.createElement(Route, { path: "/chat/", component: ChatRoute }),
    React.createElement(Route, { path: "/bekreft_epost/", component: ConfirmEmailPage }),
    React.createElement(Route, { path: "/glemt_passord/", component: ForgotPasswordPage }),
    React.createElement(Route, { path: "/bokvarslinger/avslutt", component: NotificationsUnsubscribePage }),
    React.createElement(Route, { exact: true, path: ['/hjelp', '/hjelp/'], component: HelpPage }),
    React.createElement(Route, { exact: true, path: ['/om', '/om/'], component: AboutPage }),
    React.createElement(Route, { exact: true, path: ['/betingelser', '/betingelser/'], component: TermsPage }),
    React.createElement(Route, { exact: true, path: ['/personvern', '/personvern/'], component: PrivacyPage }),
    React.createElement(Route, { exact: true, path: ['/annonseregler', '/annonseregler/'], component: ClassifiedRulesPage }),
    React.createElement(Route, { exact: true, path: ['/strekkode', '/strekkode/'], component: BarcodeScannerPage }),
    React.createElement(Route, { exact: true, path: ['/mine_annonser', '/mine_annonser/'], component: MineClassifiedsRoute }),
    React.createElement(Route, { exact: true, path: ['/administrer_annonser', '/administrer_annonser/'], component: AdministerClassifiedsRoute }),
    React.createElement(Route, { exact: true, path: ['/administrer_annonser/:status', '/administrer_annonser/:status/'], component: AdministerClassifiedsRoute }),
    React.createElement(Route, { exact: true, path: ['/bokvarslinger', '/bokvarslinger/'], component: NotificationsRoute }),
    React.createElement(Route, { exact: true, path: ['/innstillinger', '/innstillinger/'], component: SettingsRoute }),
    React.createElement(Route, { exact: true, path: ['/endre_passord', '/endre_passord/'], component: ChangePasswordRoute }),
    React.createElement(Route, { exact: true, path: ['/endre_passord/:code', '/endre_passord/:code/'], component: ChangePasswordRoute }),
    React.createElement(Route, { exact: true, path: ['/selger/:id(\\d+)', '/selger/:id(\\d+)/'], component: SellerRoute }),
    React.createElement(Route, { component: NotFoundPage }))); };
var StandaloneApp = function () { return (React.createElement(QueryClientProvider, { client: queryClient },
    React.createElement(ReactContextWrapper, null,
        React.createElement(BrowserRouter, null,
            React.createElement("div", { className: "standalone-react" },
                React.createElement(StandaloneHeader, null),
                React.createElement("main", { className: "standalone-react__main" },
                    React.createElement(StandaloneRoutes, null)),
                React.createElement(StandaloneFooter, null)))))); };
export default StandaloneApp;
